import React, { useState } from "react";
import { useInstallPrompt } from "../../hooks/useInstallPrompt";
import {
  XMarkIcon,
  ArrowUpOnSquareIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";

const InstallPrompt: React.FC = () => {
  const { isPromptVisible, environment, dismissPrompt, promptInstall } =
    useInstallPrompt();
  const [isCopied, setIsCopied] = useState(false);

  if (!isPromptVisible) return null;
  if (
    environment === "installed" ||
    environment === "desktop" ||
    environment === "unknown"
  )
    return null;

  const handleCopyLink = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(window.location.href);
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 3000);
      } else {
        // Fallback for HTTP environments like testing on local IP
        const textArea = document.createElement("textarea");
        textArea.value = window.location.href;
        // Prevent iOS keyboard from opening
        textArea.readOnly = true;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        textArea.style.top = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand("copy");
        textArea.remove();
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 3000);
      }
    } catch (err) {
      console.error("Failed to copy", err);
      alert(
        "Please copy the URL manually from your address bar and open it in Safari.",
      );
    }
  };

  return (
    <div className="fixed bottom-0 inset-x-0 pb-4 sm:pb-6 z-50 px-4">
      <div className="bg-indigo-600 rounded-lg shadow-lg p-4 pt-5 sm:p-6 relative text-white">
        <button
          onClick={dismissPrompt}
          className="absolute top-2 right-2 text-indigo-200 hover:text-white transition-colors p-1"
          aria-label="Dismiss"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>

        <div className="flex flex-col items-center text-center space-y-3">
          <img
            src="/logo192.png"
            alt="Superset Logo"
            className="w-12 h-12 rounded-lg shadow-sm"
          />

          <h3 className="font-semibold text-lg">Install Superset</h3>

          {environment === "android-chrome" && (
            <>
              <p className="text-sm text-indigo-100">
                Install our app on your home screen for quick and easy access
                when you're at the gym.
              </p>
              <button
                onClick={promptInstall}
                className="w-full bg-white text-indigo-600 font-semibold py-2 px-4 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 mt-2"
              >
                Install App
              </button>
            </>
          )}

          {environment === "ios-safari" && (
            <div className="text-sm text-indigo-100 flex flex-col items-center w-full">
              <p className="mb-3">
                Install our app on your home screen for quick and easy access.
              </p>
              <div className="bg-indigo-700/50 rounded-md p-3 w-full flex flex-col space-y-2 text-left">
                <span className="flex items-center">
                  1. Tap the Share icon{" "}
                  <ArrowUpOnSquareIcon className="h-5 w-5 mx-1.5 inline-block text-blue-400" />{" "}
                  below.
                </span>
                <div className="flex flex-col space-y-1">
                  <span>2. Scroll down and select:</span>
                  <span className="flex items-center pl-4">
                    "Add to Home Screen"{" "}
                    <span className="flex items-center justify-center p-0.5 mx-2 border-2 border-gray-300 rounded">
                      <PlusIcon className="h-4 w-4 text-gray-300 stroke-2" />
                    </span>
                  </span>
                </div>
              </div>
            </div>
          )}

          {environment === "ios-other" && (
            <div className="text-sm text-indigo-100 flex flex-col items-center w-full">
              <p className="mb-2">
                To install this app on iOS, please open it in{" "}
                <strong>Safari</strong>.
              </p>
              <button
                onClick={handleCopyLink}
                className={`w-full font-medium py-2 px-4 rounded-md transition-colors mt-2 border ${
                  isCopied
                    ? "bg-green-600 text-white border-green-500 hover:bg-green-700"
                    : "bg-indigo-700 text-white border-indigo-500 hover:bg-indigo-800"
                }`}
              >
                {isCopied ? "Copied! Open Safari to paste" : "Copy App Link"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InstallPrompt;
